import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Briefcase, Search, MapPin, DollarSign, Building2, ArrowRight, Filter, Facebook, Instagram, Home } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';
import Footer from '../components/Footer';

const JobsPublicPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    location: '',
    spanish_level: '',
    sector: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filters.location) params.append('location', filters.location);
      if (filters.spanish_level) params.append('spanish_level', filters.spanish_level);
      if (filters.sector) params.append('sector', filters.sector);

      const response = await fetch(`${API}/jobs?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Error al cargar ofertas');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJobs();
  };

  const getSpanishLevelBadge = (level) => {
    const variants = {
      native: { color: 'bg-red-100 text-red-700', label: 'Nativo' },
      advanced: { color: 'bg-red-100 text-red-600', label: 'Avanzado' },
      intermediate: { color: 'bg-orange-100 text-orange-700', label: 'Intermedio' },
      basic: { color: 'bg-amber-100 text-amber-700', label: 'Básico' },
      'not-required': { color: 'bg-gray-100 text-gray-700', label: 'No requerido' },
    };
    const variant = variants[level] || variants['not-required'];
    return <Badge className={`${variant.color} font-medium`}>{variant.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <Briefcase className="w-7 h-7 text-red-600" />
              <span className="text-xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">
                JobSuiza
              </span>
            </Link>

            <div className="flex items-center space-x-2 sm:space-x-4">
              <Link to="/">
                <Button variant="ghost" size="sm" data-testid="back-home-btn">
                  <Home className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Inicio</span>
                </Button>
              </Link>
              <Link to="/anuncios">
                <Button variant="ghost" size="sm">Tablón</Button>
              </Link>
              
              {/* Social Media Icons - Hidden on small screens */}
              <div className="hidden md:flex items-center space-x-2">
                <a 
                  href="https://www.facebook.com/jobsuizaoficial" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-8 h-8 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="w-4 h-4" />
                </a>
                <a 
                  href="https://www.instagram.com/jobsuizaoficial" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 hover:from-purple-700 hover:via-pink-600 hover:to-orange-500 text-white rounded-full transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-4 h-4" />
                </a>
              </div>
              
              <Link to="/auth">
                <Button variant="outline" size="sm">Entrar</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4">Ofertas de Trabajo en Suiza</h1>
          <p className="text-xl text-slate-300">Encuentra tu próxima oportunidad laboral para hispanohablantes</p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="mb-8">
          <CardContent className="p-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <Input
                    placeholder="Buscar por título, empresa o palabras clave..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 h-12"
                  />
                </div>
                <Button type="submit" className="h-12 px-8 bg-red-600 hover:bg-red-700">
                  Buscar
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="h-12"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filtros
                </Button>
              </div>

              {showFilters && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                  <Select value={filters.location} onValueChange={(value) => setFilters({...filters, location: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Ubicación" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Todas</SelectItem>
                      <SelectItem value="Zurich">Zúrich</SelectItem>
                      <SelectItem value="Geneva">Ginebra</SelectItem>
                      <SelectItem value="Basel">Basilea</SelectItem>
                      <SelectItem value="Bern">Berna</SelectItem>
                      <SelectItem value="Lausanne">Lausana</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={filters.spanish_level} onValueChange={(value) => setFilters({...filters, spanish_level: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Nivel de español" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Todos</SelectItem>
                      <SelectItem value="native">Nativo</SelectItem>
                      <SelectItem value="advanced">Avanzado</SelectItem>
                      <SelectItem value="intermediate">Intermedio</SelectItem>
                      <SelectItem value="basic">Básico</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={filters.sector} onValueChange={(value) => setFilters({...filters, sector: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sector" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Todos</SelectItem>
                      <SelectItem value="technology">Tecnología</SelectItem>
                      <SelectItem value="finance">Finanzas</SelectItem>
                      <SelectItem value="healthcare">Salud</SelectItem>
                      <SelectItem value="hospitality">Hostelería</SelectItem>
                      <SelectItem value="education">Educación</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Results count */}
        <div className="mb-6">
          <p className="text-slate-600">
            {loading ? 'Cargando...' : `${jobs.length} ofertas encontradas`}
          </p>
        </div>

        {/* Job listings */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Cargando ofertas...</p>
          </div>
        ) : jobs.length === 0 ? (
          <Card className="p-12 text-center">
            <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No hay ofertas disponibles</h3>
            <p className="text-slate-600">Intenta con otros filtros de búsqueda</p>
          </Card>
        ) : (
          <div className="grid gap-6">
            {jobs.map((job, index) => (
              <Link to={`/ofertas/${job.id}`} key={job.id}>
                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {job.is_featured && (
                            <Badge className="bg-amber-100 text-amber-700">Destacado</Badge>
                          )}
                          <h3 className="text-xl font-bold text-slate-900">{job.title}</h3>
                        </div>
                        
                        <div className="flex items-center text-red-600 mb-3">
                          <Building2 className="w-4 h-4 mr-2" />
                          <span className="font-medium">{job.company}</span>
                        </div>

                        <div className="flex flex-wrap gap-4 text-slate-600 mb-4">
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            {job.location}
                          </div>
                          {job.salary && (
                            <div className="flex items-center">
                              <DollarSign className="w-4 h-4 mr-1" />
                              {job.salary}
                            </div>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline">{job.contract_type || 'Tiempo completo'}</Badge>
                          <Badge variant="outline">{job.sector || 'General'}</Badge>
                          {getSpanishLevelBadge(job.spanish_level)}
                        </div>
                      </div>

                      <div className="flex items-center text-red-600 ml-4">
                        <span className="text-sm font-medium mr-2">Ver oferta</span>
                        <ArrowRight className="w-5 h-5" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default JobsPublicPage;
